import { runtime } from './runtime.js';
import { virtualPage } from './snippet.js';

/**
 * The page the visitor is on, as every page question and URL rule reads it: the address bar, unless a
 * stage page declared the provider's page it stands in for (see virtualPage in snippet.js).
 * @returns {{host: string, pathname: string, search: string}}
 */
export function currentPage() {
    if (virtualPage) {
        return virtualPage;
    }

    return { host: location.host || '', pathname: location.pathname || '/', search: location.search || '' };
}

/**
 * Mirror of WidgetPagePaths.NormalizePath (Feedbax.Domain) — the two must stay in step: no query or
 * fragment, a leading slash, no trailing slash (root stays "/"), lowercase.
 * @param {string} path - A pathname or the path half of a page URL
 * @returns {string}
 */
export function normalizePagePath(path) {
    let value = typeof path === 'string' ? path.trim() : '';

    const cut = value.search(/[?#]/);
    if (cut >= 0) {
        value = value.slice(0, cut);
    }

    if (value.charAt(0) !== '/') {
        value = '/' + value;
    }

    while (value.length > 1 && value.charAt(value.length - 1) === '/') {
        value = value.slice(0, -1);
    }

    return value.toLowerCase();
}

/** Mirrors WidgetPageQueryKeys.ValueMaxLength; a section slug is short, anything longer is an id. */
const pageQueryValueMaxLength = 60;

/**
 * One page key out of the payload, tidied for comparison. Not normalizePagePath: the server files a
 * question under the KEY, so cutting at the "?" here would throw the screen away and every screen of a
 * path would collapse back onto the first question written for any of them — the very thing the setting
 * exists to fix.
 * Nothing is filtered or re-sorted: the server built this key from the widget's own list and the loader
 * only has to fold it the same way it folds an address (casing, the trailing slash, no fragment).
 * @param {string} value - The path or key a served entry carries
 * @returns {string}
 */
export function normalizePageKey(value) {
    const text = typeof value === 'string' ? value.trim() : '';
    const queryAt = text.indexOf('?');

    if (queryAt < 0) {
        return normalizePagePath(text);
    }

    const path = normalizePagePath(text.slice(0, queryAt));
    const query = text.slice(queryAt + 1).split('#')[0].trim().toLowerCase();

    return query.length > 0 ? path + '?' + query : path;
}

/**
 * Whether a served key names the page the visitor is on. Equal keys match, and so does a served key whose
 * path has a "*" segment, which stands for any one non-empty segment. The server writes that placeholder
 * in one place only: our own LeadWin dashboard, whose address names the customer's agent
 * (/de/dashboard/{handle}?section=api), so every customer's view of a section shares one question
 * (WidgetDashboardPageKeys). The query has to match exactly either way.
 * @param {string} served - The key a served entry carries, normalized
 * @param {string} current - The key of the page the visitor is on (buildPageKey)
 * @returns {boolean}
 */
export function pageKeyMatches(served, current) {
    if (served === current) {
        return true;
    }

    if (typeof served !== 'string' || typeof current !== 'string' || served.indexOf('*') < 0) {
        return false;
    }

    const servedAt = served.indexOf('?');
    const currentAt = current.indexOf('?');

    if ((servedAt < 0 ? '' : served.slice(servedAt)) !== (currentAt < 0 ? '' : current.slice(currentAt))) {
        return false;
    }

    const servedParts = (servedAt < 0 ? served : served.slice(0, servedAt)).split('/');
    const currentParts = (currentAt < 0 ? current : current.slice(0, currentAt)).split('/');

    return servedParts.length === currentParts.length
        && servedParts.every(function (part, index) {
            return part === currentParts[index] || (part === '*' && currentParts[index] !== '');
        });
}

/**
 * Mirror of WidgetPageQueryKeys.BuildPageKey (Feedbax.Domain) — the two must stay in step: one of them
 * writes the key a question is filed under, the other looks it up.
 *
 * The page a visitor is on, as this widget files it: the normalized path, and behind it the query
 * parameters the provider named as naming a screen. Without named parameters this IS normalizePagePath,
 * which is what every widget that never opened the setting keeps getting — the query is noise a page
 * carries around (?utm_source=…, ?page=2, a sort order), and covering one page a dozen times over would
 * cost a model call each time.
 * The kept parameters are sorted, so the same screen yields the same key however the application ordered
 * them in the address bar.
 * @param {Array<string>} keys - The provider's parameter names; empty or missing means "path only"
 * @returns {string}
 */
export function buildPageKey(keys) {
    const page = currentPage();
    const path = normalizePagePath(page.pathname);

    if (!Array.isArray(keys) || keys.length === 0) {
        return path;
    }

    const kept = [];

    try {
        const search = page.search.replace(/^\?/, '');

        for (const pair of search.split('&')) {
            const separator = pair.indexOf('=');
            if (separator <= 0) {
                continue; // A bare flag names no screen, and "=value" names no parameter.
            }

            const name = decodeURIComponent(pair.slice(0, separator)).trim().toLowerCase();
            if (keys.indexOf(name) < 0) {
                continue;
            }

            const value = decodeURIComponent(pair.slice(separator + 1)).trim().toLowerCase();
            if (value.length > 0) {
                kept.push(name + '=' + value.slice(0, pageQueryValueMaxLength));
            }
        }
    } catch (e) {
        return path; // A malformed escape in somebody's URL must never cost the page its question.
    }

    if (kept.length === 0) {
        return path;
    }

    kept.sort();

    return path + '?' + kept.join('&');
}

/**
 * The host that referred this page, or '' for a direct arrival and for any navigation within the
 * embedding site itself. Host only, never the full referring URL: the assistant is told which channel
 * a visitor came through, and a referring path belongs to a website that is not ours to pass on.
 * Internal referrers are dropped here rather than sent and filtered server-side, for the same reason.
 * @returns {string}
 */
export function externalReferrerHost() {
    try {
        const referrer = document.referrer;
        if (!referrer) {
            return '';
        }

        const host = new URL(referrer).hostname.toLowerCase();
        const own = location.hostname.toLowerCase();

        if (host === own || host.endsWith('.' + own) || own.endsWith('.' + host)) {
            return ''; // Same site — internal navigation says nothing about where they came from.
        }

        return host;
    } catch (e) {
        return ''; // Opaque or malformed referrer — no channel, no line in the prompt.
    }
}

/* What "mobile" means for an overlay: not the input method but the room it takes. The same bubble that
 * uses a corner of a desktop page covers a good part of a phone screen, which is what a contractor who
 * switches it off there is reacting to. 640px is the app's own narrow breakpoint. */
export const mobileViewportQuery = '(max-width: 640px)';

export function isMobileViewport() {
    try {
        return window.matchMedia(mobileViewportQuery).matches;
    } catch {
        return false; // No matchMedia (or it threw): treat the screen as roomy and show the bubble.
    }
}

/* One history patch for the whole loader. Analytics closes a pageview on a route change and the nudges
 * may want to offer themselves again on the new page; patching pushState once per feature would nest
 * wrappers on the host page's own history object for no reason. */
const pathChangeHandlers = [];
let pathChangeHooked = false;

export function onPathChange(handler) {
    pathChangeHandlers.push(handler);

    if (pathChangeHooked) {
        return;
    }
    pathChangeHooked = true;

    try {
        const notify = function () {
            // The KEY, not the bare pathname: on a site whose screens live in query parameters a section
            // switch changes nothing else, and every subscriber here exists to notice a new screen. For
            // every widget that named no parameters the key IS the pathname, so nothing changes for them.
            const path = buildPageKey(runtime.pageQueryKeys);
            pathChangeHandlers.forEach(function (subscriber) {
                subscriber(path);
            });
        };
        const originalPushState = history.pushState;
        history.pushState = function () {
            const result = originalPushState.apply(this, arguments);
            setTimeout(notify, 0);
            return result;
        };
        window.addEventListener('popstate', function () {
            setTimeout(notify, 0);
        });
    } catch { /* SPA support is optional — full-page navigations still work */ }
}
